
import { ERROR_MESSAGES } from '../../const/AppConst';
import useAppState from '../../context/AppContext';
import { ChangeEvent, useEffect, useState } from 'react';
import LoaderModal from '../../modals/common/LoaderModal';
import MgpMessage from '../../elements/MgpMessage/MgpMessage';
import { accessJsonAttr, getCompressFile, getFilename, isAcceptedFileSize, isMobile, isValidFileSize, isValidFileType } from '../../utils/utils';
import UploadFilePreviewModal from '../../modals/common/UploadFilePreviewModal';
import { deleteDocument, downloadUploadedDocument, uploadFamilyDocument } from '../../services/CustomerApiService';
import { AxiosError } from 'axios';
import { IApiErrors, IDocumentType, IUploadedDoc, uploadDocFncType } from '../../context/model';
import UploadFromPhoneButton from '../common/UploadFromPhoneButton';
import ListOfFileUploaded from '../common/ListOfFileUploaded';
import QrCodeModal from '../../modals/common/QrCodeModal';
import UploadMultipleComponent from '../common/UploadMultipleComponent';
import UploadDocumentButton from '../common/UploadDocumentButton';
import MgpInputFileUpload from '../../elements/MgpInputFileUpload/MgpInputFileUpload';
import { UseFormSetValue } from 'react-hook-form';
import MgpTooltip from '../../elements/MgpTooltip/MgpTooltip';

interface UploadDocumentTemplateProps {
    requiredDocuments:  IDocumentType,
    errors: any,
    isUploadMultipe: boolean,
    setValue?: UseFormSetValue<any>,
    infoMsg?: string,
    hasRequired?: boolean,
    tooltip?: string
}

export default function UploadDocumentTemplate({
        requiredDocuments, 
        errors, 
        isUploadMultipe, 
        hasRequired = true,
        setValue,
        infoMsg,
        tooltip
}: Readonly<UploadDocumentTemplateProps>) {

  const {
    state: { jwtToken, proposition, familyDoc},
    dispatch
  } = useAppState();
  const formKey = requiredDocuments.code
  const filteredUploadedDoc =  familyDoc?.filter(
    doc => doc.fileType === requiredDocuments.code
  );
  
  const [errorMsg, setErrorMsg] = useState<string | null>();
  const [loading, setLoading] = useState<boolean>(false);
  const [errorFileTypeMsg, setErrorFileTypeMsg] = useState<any>({});
  const [errorFileSizeMsg, setErrorFileSizeMsg] = useState<boolean | null>();
  const [file, setFile] = useState<File | null>(null);
  const [openPreviewFile, setOpenPreviewFile] = useState<boolean>(false);
  const [isShowModal, setIsShowModal] = useState<boolean>(false);
  const [fileType, setFileType] = useState<string>('');
  const [showUploadMultiple, setShowUploadMultiple] = useState<boolean>(false);
  const [hasMultipleFile, setHasMultipleFile] = useState<boolean>(false);
  const [currentDocument, setCurrentDocument] = useState<IUploadedDoc[]>(filteredUploadedDoc ?? [])

  useEffect(()=>{
    setValue?.(formKey, currentDocument)
  }, [currentDocument])

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
      try {
        const uploadedfile = event.target.files ? event.target.files[0] : null;
        if (uploadedfile) {
          if (!isValidFileType(uploadedfile)) {
            setErrorFileTypeMsg(true)
          } else if (!isValidFileSize(uploadedfile)) {
            setErrorFileSizeMsg(true)
          } else if (!isAcceptedFileSize(uploadedfile)) {
            try {
              const token = jwtToken!.accessToken;
              const newFile = await getCompressFile(token, uploadedfile);
              setFile(newFile);
              setOpenPreviewFile(true);
            } catch(e) {
              setErrorMsg("Suite à un incident technique, nous ne pouvons pas uploader votre fichier. Veuillez sélectionner à nouveau.");
            }  
          } else {
            setFile(uploadedfile);
            setOpenPreviewFile(true);
          }
        }
      } catch (error) {
        const err = error as AxiosError;
        console.log(err);
        if(err.response && err.response.status === 400) {
          const errorData = err.response?.data as IApiErrors;
          setErrorMsg("Erreur: " + errorData.message);
        } else {
          setErrorMsg("Une erreur technique est survenue ");
        }
      }
    
    };
  
  const onUploadAction = async (fileToUpload: File) => {
    setErrorMsg(null);
    setErrorFileTypeMsg(false);
    setLoading(true);
    try {
      const token = jwtToken!.accessToken; 
   
      const res = await uploadFamilyDocument(
        token,  
        proposition!.propositionId, 
        fileToUpload, 
        fileType
      );

      const newDoc = {
        id: res?.data.id, 
        name: res?.data.name, 
        fileName: res?.data.fileName, 
        fileType: requiredDocuments.code
      }
      
      const newList = [...currentDocument, newDoc]
      setCurrentDocument(newList)
      dispatch({
        type: 'UPLOAD_FAMILY_DOCUMENT',
        familyDoc: [...familyDoc, newDoc]
      })

      setShowUploadMultiple(false);
      setLoading(false);
      dispatch({type: 'SHOW_MODAL', showModal: false});

    } catch (error) {
      setLoading(false);
      setShowUploadMultiple(false);
      const err = error as AxiosError;
      console.log(err);

      if(err.response && err.response.status === 400) {
        const errorData = err.response?.data as IApiErrors;
        setErrorMsg("Erreur: " + errorData.message);
      } else {
        setErrorMsg("Une erreur technique est survenue ");
      }
    }
  }

  const onDeleteDocLocal = (docId: string) =>  {
    if(currentDocument.length !== 0){
        const filteredDocuments = currentDocument.filter(el => el.id !== docId);
        setCurrentDocument([...filteredDocuments])
        dispatch({
          type: 'UPLOAD_FAMILY_DOCUMENT', 
          familyDoc: [...familyDoc.filter((el:any) => el.id !== docId)]
        })
        setLoading(false);
        setErrorMsg(null);
    }
  }

  const handleDeleteFile = async (docId: string) =>  {
    setLoading(true);
    try {
      await deleteDocument(
        jwtToken!.accessToken,
        proposition!.propositionId, 
        docId
      );

      onDeleteDocLocal(docId);

    } catch (error) {
      setLoading(false);
      const err = error as AxiosError;
      console.log(err);
      if(err.response && err.response.status === 400) {
        const errorData = err.response?.data as IApiErrors;
        setErrorMsg("Erreur: " + errorData.message);
      } else if (err.response && err.response.status === 404) {
        onDeleteDocLocal(docId);
      } else {
        setErrorMsg("Une erreur technique est survenue ");
      }
    }
  };

  const onDownloadDocument = async (documentId: string | undefined) => {
    try {
      const token = jwtToken!.accessToken;     
      const res = await downloadUploadedDocument(
        token, 
        proposition!.propositionId,
         documentId
      );
      const content = new Blob(
        [res.data], 
        {type: res.headers['content-type']}
      );
      const filename= getFilename(res.headers);
      const newFile = new File([content], filename)
      setFile(newFile);
      setOpenPreviewFile(true);
      dispatch({type: 'SHOW_MODAL', showModal: true});


    } catch (error) {
      setLoading(false);
      const err = error as AxiosError;
      console.log(err);

      if(err.response && err.response.status === 400) {
        const errorData = err.response?.data as IApiErrors;
        setErrorMsg("Erreur: " + errorData.message);
      } else if (err.response && err.response.status === 404) {
        setErrorMsg(ERROR_MESSAGES.documentNotFound);
      } else {
        setErrorMsg("Une erreur technique est survenue ");
      }
    }
  }
  const closeModal = ()=> {
    setOpenPreviewFile(false);
    dispatch({type: 'SHOW_MODAL', showModal: false});
  }

  const showQrCodeModal = (docCode: string) => {
    setFileType(docCode);
    setIsShowModal(true);
  }

  const closeQrModal = () => {
    setIsShowModal(false);
    setFileType('');
  }

  // listen and detect realtime from upload file (mobile) thanks to eventId
 const onEventMessage = (message: any, type: string) => {
    if(type === 'upload') {
      const docs = [...familyDoc, {id: message.id, name: message.name, fileName: message.fileName, fileType: requiredDocuments.code}]
      dispatch({type: 'UPLOAD_FAMILY_DOCUMENT', familyDoc: docs});
      const updatedFiles = docs.filter(doc=> doc.fileType === requiredDocuments.code);
      setCurrentDocument([...updatedFiles])
      setLoading(false);
      setIsShowModal(false);
    }
  }

  return (             
      <>       
      {errorMsg && 
        <MgpMessage 
          type="danger" 
          message={errorMsg} 
          hasCloseBtn={true}
        />}
      {hasMultipleFile && 
        <MgpMessage 
          type='info'
          message='Vos fichiers ont bien été fusionnés et téléchargés' 
          hasCloseBtn={true} 
        />
      }
      {/* show uploaded file */}
      {
        currentDocument?.map((doc, i) => (
          <div className='form-group pb-xl' key={i+doc.name}>
            <div className='form-row pb-xl' >
              <div className='form-group mw-50'>
              <ListOfFileUploaded
                fileName={doc.name ?? doc.fileName ?? 'Votre document'}
                onDownloadDocument={() => onDownloadDocument(doc.id)}
                onDeleteDocument={() =>  handleDeleteFile(doc.id)}
                />
              </div>
            </div>
          </div>
        ))
      }
  
      <div>
        {/* 2 options for uploading file */}
            {
              currentDocument.length === 0 &&             
              <div>
                <div className='form-row'>
                  <div className='form-group mw-70'>
                      {errorFileTypeMsg[requiredDocuments.code]
                      && <p className='text blanc p-sm bg-fushia'>
                        {ERROR_MESSAGES.fileTypeMsg}
                        </p>}

                  </div>
                </div> 
                <div>  
                  <p>{requiredDocuments.label}{hasRequired &&<span className="fushia"> (*)</span>}{tooltip &&
                        tooltip?.length > 0 &&
                        <MgpTooltip text={tooltip} maxWidth="550px">
                             <p 
                                className='info-badge info-icon' 
                                style={{
                                    marginTop: '-14px', 
                                    height: '15px', 
                                    width: '15px',
                                     fontSize: '11px'
                                }}>i</p>
                        </MgpTooltip>}</p>
                 
                  <div className="form-row">
                    <div className='form-group mw-30'>
                      {
                        isUploadMultipe ?

                        <UploadDocumentButton onClick={() =>  {
                            setFileType(
                              requiredDocuments.label.includes("handicap") 
                              ? "CHILD_HANDICAP_PROOF" 
                              : requiredDocuments.code
                            );
                            dispatch({type: 'SHOW_MODAL', showModal: true});
                            setShowUploadMultiple(true)}}
                            
                        />
                        :
                        <MgpInputFileUpload
                            id="file"
                            onChange={(e) => handleFileChange(e)}                    
                        /> 
                      }          
                    </div>
                    {
                      !isMobile() 
                      && <div className="form-group mw-5 ta-center">
                            <p className='fz-lg' style={{marginTop: "100px"}}>ou</p>
                         </div>
                    }
                    {!isMobile() && 
                    <div className='form-group mw-30'>
                      <UploadFromPhoneButton 
                        onClick={() => showQrCodeModal(requiredDocuments.code)}
                      /> 
                    </div>}
                
                  </div>
                </div>
                {infoMsg && <p className='fz-sm'>{infoMsg}</p>}

                {
                  ( errors 
                  && accessJsonAttr(errors, formKey) )
                  && <p className="error mt-sm fushia">
                       (*) Merci de télécharger tous les documents demandés
                     </p>
                } 
              </div>       
          }
        {openPreviewFile
            &&file
            &&<UploadFilePreviewModal 
                    file={file} 
                    isOpen={openPreviewFile} 
                    onCloseModal={closeModal} 
                    showButton={false}/>} 

        {showUploadMultiple 
            && <UploadMultipleComponent 
                    isOpen={showUploadMultiple} 
                    onCloseModal={() => {
                      setShowUploadMultiple(false);
                      dispatch({type: 'SHOW_MODAL', showModal: false});
                    }} 
                    docType={fileType} onUploadAction={onUploadAction} 
                    setHasMultipleFile={(response : boolean) => setHasMultipleFile(response) }
        />}

        </div>
          {loading && <LoaderModal hasMessage={false}/>}
        <div>
        
        {isShowModal
          && <QrCodeModal 
                isOpen={isShowModal} 
                onCloseModal={closeQrModal} 
                docCode={fileType} 
                onEventMessage={onEventMessage}
                />}
      </div>      
    </>
  )
}


